import { LockClosedIcon } from "@heroicons/react/24/outline";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { ReactComponent as EditPencil } from "../../Assets/Svg/EditPencil.svg";
import { ReactComponent as Trash } from "../../Assets/Svg/Trash.svg";
import {
  useSubworkspaceStore,
  useUserStore,
  useWorkspaceStore,
} from "../../Context/store";
import { useRolePermissions } from "../../hooks/usePermission";
import useDebounce from "../../Services/useDebounce";
import { getWorkspaceRole } from "../../Utils/Helper";
import { useGetMembersList } from "../../Web-Hooks/RoleManagement/use-rolemanagement";
import {
  useRemoveUserFromSubWorkspace,
  useSubWorkspacesByWorkspaceId,
} from "../../Web-Hooks/Workspace/use-subworkspace";
import AddEditRole from "../Popup/AddEditUser";
import InfoPopup from "../Popup/InfoPopup";
import ActionButton from "../UI/Buttons/ActionButton";
import BasicButton from "../UI/Buttons/BasicButton";
import SearchField from "../UI/Inputs/SearchField";
import BasicSelect from "../UI/Select/BasicSelect";
import TableWithPagination from "./TableWithPagination";

function RoleManagementTableFactored() {
  const { selectedSubWorkspace } = useSubworkspaceStore();
  const { selectedWorkspace } = useWorkspaceStore();
  const [open, setOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(false);
  const [profile, setProfile] = useState({});
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const { user: loggedInUser } = useUserStore();
  const [search, setSearch] = useState("");
  const debouncedSearchTerm = useDebounce(search, 600);
  const [filter, setFilters] = useState();
  const { hasPermission, canManageRole, getRoleData, getLimit } =
    useRolePermissions();
  const {
    mutateAsync: removeUserFromSubWorkspace,
    isPending: isDeleteUserSucceeded,
  } = useRemoveUserFromSubWorkspace();

  const { data: subworkspaceList } =
    useSubWorkspacesByWorkspaceId(
      selectedWorkspace?.workSpaceId,
      loggedInUser?.userId
    );

  const { data, isLoading } = useGetMembersList(
    pagination?.pageIndex + 1,
    pagination?.pageSize,
    loggedInUser?.userId,
    filter?.workspace?.value || selectedWorkspace?.workSpaceId,
    filter?.forum?.value || "",
    debouncedSearchTerm
  );

  const AlreadyAddedMembers = data?.usersWithRole?.map((item) => ({
    id: item?.userId || item?.userEmail,
    label: item?.userEmail,
    value: item?.userEmail,
    role: item?.roleName,
  }));

  useEffect(() => {
    setFilters({
      forum: {
        label: selectedSubWorkspace?.subWorkSpaceName,
        value: selectedSubWorkspace?.subWorkSpaceId,
        role: selectedSubWorkspace?.loginUserRole,
      },
      workspace: {
        label: selectedWorkspace?.workSpaceName,
        value: selectedWorkspace?.workSpaceId,
        role: getWorkspaceRole(selectedWorkspace, loggedInUser?.userId),
      },
    });
  }, [selectedWorkspace, selectedSubWorkspace, loggedInUser]);


  const WorkspaceOptions =
    [
      {
        label: selectedWorkspace?.workSpaceName,
        value: selectedWorkspace?.workSpaceId,
        role: getWorkspaceRole(selectedWorkspace, loggedInUser?.userId),
      },
    ] || [];

  const forumOptions =
    subworkspaceList?.map((subWorkspace) => {
      return {
        label: subWorkspace.subWorkSpaceName,
        value: subWorkspace.subWorkSpaceId,
        role: subWorkspace?.loginUserRole,
        totalMembers: subWorkspace?.totalMember,
      };
    }) || [];

  // const usersLength = data?.usersWithRole?.length;

  const role = filter?.workspace?.role || filter?.forum?.role;

  // const addUsersLimit = getLimit(role, "maxAddUserPerForum");
  // const isAddUserLimitExceed = usersLength >= addUsersLimit;
  const hasAccessToAddUser = role ? hasPermission(role, "addUser") : false;

  const ForumOptionsForAddUser = forumOptions?.filter((forum) =>
    filter?.forum?.label !== "All Forum"
      ? forum?.value === filter?.forum?.value
      : true
  );

  // const hasAnyForums = ForumOptionsForAddUser?.length > 0;

  const forumOptionsForEditUser = forumOptions?.filter(
    (forum) => 
      forum?.value === profile?.subWorkSpaceId
  );

  const dataRole = getRoleData(role || profile?.loggedInUserRole);
  const RoleOptions =
    dataRole?.manageRoles?.map((role) => ({
      label: role,
      value: role,
    })) || [];


  const handleDelete = async () => {
    if (!deleteId?.userId && !deleteId?.subworkspaceId) {
      toast.error("User not found");
      return;
    };
    const deleteUser = {
      LoginUserId: loggedInUser?.userId,
      SubWorkSpaceId: deleteId?.subworkspaceId,
      UserId: deleteId?.userId,
    };
    try {
      await removeUserFromSubWorkspace(deleteUser);
      setDeleteId("");
      toast.success("User deleted successfully", "success");
    } catch (err) {
      console.error(err);
    }
  };

  const columns = useMemo(
    () => [
      {
        id: "Username",
        header: "Username",
        // accessorKey: "userName",
        filterFn: "includesString",
        cell: (row) => (
          <div className="p-2">
            <p className="w-full  text-center">
              {row.row.original?.userName || row.row.original?.userEmail}
            </p>
          </div>
        ), //note: normal non-fuzzy filter column - case insensitive
      },
      {
        id: "Forum",
        header: "Forum name",
        accessorKey: "subworkSpaceName",
        filterFn: "includesString", //note: normal non-fuzzy filter column - case insensitive
      },
      {
        id: "Email",
        header: "Email",
        accessorKey: "userEmail",
        filterFn: "includesString",
        cell: (row) => (
          <div className="p-2">
            <p className="w-full  text-center">{row.getValue("userEmail")}</p>
          </div>
        ), //note: normal non-fuzzy filter column - case insensitive
      },
      {
        id: "Role",
        header: "",
        accessorKey: "roleName",
        meta: {
          filterVariant: "select",
        },
        cell: (row) => (
          <div className="p-2">
            <p className="w-full  text-center">
              {`${row.row.original?.roleName} `}
              {row.row.original?.invitationStatus === "pending" ? (
                <span className="text-debatePalette-link">(Invited)</span>
              ) : (
                ""
              )}
            </p>
          </div>
        ),
      },
      {
        id: "Assigned",
        header: "Assigned",
        accessorKey: "assignedDate",
        accessorFn: (row) => moment(row.assignedDate).format("DD MMM, YYYY"),
        filterFn: "equalsString", //note: normal non-fuzzy filter column - case insensitive
      },
      {
        id: "Action",
        header: "Action",
        cell: (row) => {
          const isLoggedInUser =
            loggedInUser?.userId === row.row.original?.userId;
          const listData = row.table.options.data;
          const findLoggedInUser = listData.find(
            (item) => item?.userId === loggedInUser?.userId
          );

          const getRoleForumWise = (forumId) => {
            const userRole = forumOptions?.find(
              (forum) => forum?.value === forumId
            )?.role;
            return userRole;
          };

          const getUserRoleSelectedForum = getRoleForumWise(
            filter?.forum?.value
          );

          const workspaceRole =
            loggedInUser?.role === "Admin" || loggedInUser?.role === "SubAdmin"
              ? loggedInUser?.role
              : "";

          const hasAccessToEditUser = hasPermission(
            filter?.forum?.value ? getUserRoleSelectedForum : workspaceRole,
            "editUser"
          );

          return (
            <div className="flex gap-2 justify-center">
              {!isLoggedInUser &&
              hasAccessToEditUser &&
              canManageRole(
                filter?.forum?.value ? getUserRoleSelectedForum : workspaceRole,
                row.row.original?.roleName
              ) &&
              row.row.original?.invitationStatus !== "pending" ? (
                <>
                  <ActionButton
                    title={"Edit"}
                    SVG={EditPencil}
                    isText={false}
                    onClick={() => {
                      const member = row.row.original;
                      const selectedMember = {
                        userId: member?.userId,
                        workSpaceId: selectedSubWorkspace?.workSpaceId,
                        subWorkSpaceId: member?.subworkspaceId,
                        userName: member?.userName || member?.userEmail,
                        email: member?.userEmail,
                        role: member?.roleName,
                        isAccept: true,
                        assigned: new Date(),
                        loggedInUserRole: findLoggedInUser?.roleName,
                      };
                      setProfile(selectedMember);
                      setOpen("Edit");
                    }}
                  />
                  <ActionButton
                    title={"Delete"}
                    SVG={Trash}
                    isText={false}
                    onClick={() => {
                      setDeleteId(row.row.original);
                    }}
                  />
                </>
              ) : (
                <LockClosedIcon className="h-5 w-5 text-debatePalette-buttonBorder cursor-not-allowed" />
              )}
            </div>
          );
        },
      },
    ], // eslint-disable-next-line
    [data, loggedInUser, filter?.forum?.value, filter?.workspace?.value, subworkspaceList]
  );


  const handleAddNewMember = () => {
    // const needToUpgradePlan = filter?.forum?.value
    //   ? isAddUserLimitExceed
    //   : !hasAnyForums;
    // if (needToUpgradePlan) {
    //   handleUpgradePlan(ADD_USERS, () => {
    //     navigate(`/upgrade-plan?plan=base-plan`);
    //   });
    //   return;
    // }
    setProfile({
      workSpaceId:
        filter?.workspace?.value || selectedSubWorkspace?.workSpaceId,
      subWorkSpaceId:
        filter?.forum?.value ||
        ForumOptionsForAddUser[0]?.value ||
        selectedSubWorkspace?.subWorkSpaceId,
      role: "Member",
    });

    setOpen("add");
  };

  return (
    <div className="w-full">
      <div className="flex w-full items-center justify-between gap-3 flex-wrap">
        <div className="lg:w-auto">
          <div className="flex w-full flex-col lg:flex-row gap-2">
            <SearchField
              id="search"
              name="search-member"
              search={search}
              setSearch={setSearch}
            />
            <BasicSelect
              id="workspace"
              Options={WorkspaceOptions}
              value={
                filter?.workspace || {
                  label: selectedWorkspace?.workSpaceName,
                  value: selectedWorkspace?.workSpaceId,
                }
              }
              disabled={true}
              onChange={(value) => {
                setFilters({
                  workspace: value,
                  forum: { label: "All Forum", value: "" },
                });
              }}
            />
            <BasicSelect
              id="forum"
              Options={[
                {
                  label: "All Forum",
                  value: "",
                },
                ...forumOptions,
              ]}
              value={
                filter?.forum || {
                  label: "All Forum",
                  value: "",
                }
              }
              onChange={(value) => {
                setFilters({ ...filter, forum: value });
              }}
              // loading={isForumsLoading}
            />
          </div>
        </div>
        <div className="flex max-w-20 lg:w-full item-center justify-end">
          {hasAccessToAddUser && (
            <BasicButton
              id={"add-new-member"}
              color="primary"
              className={"px-4 py-2"}
              onClick={() => handleAddNewMember()}
            >
              + Add
            </BasicButton>
          )}
        </div>
      </div>

      <TableWithPagination
        columns={!isLoading ? columns : []}
        data={!isLoading ? data?.usersWithRole : []}
        rowCount={data?.totalRowCount}
        pagination={pagination}
        setPagination={setPagination}
        isLoading={isLoading}
      />

      <AddEditRole
        open={!!open}
        contacts={AlreadyAddedMembers}
        onClose={() => setOpen("")}
        profile={profile}
        action={open === "add" ? "add" : "edit"}
        roleOptions={RoleOptions}
        forumOptions={
          open === "add" ? ForumOptionsForAddUser : forumOptionsForEditUser
        }
        fromRoleManagement={true}
        loggedInUser={loggedInUser}
        onSave={() => {
          setProfile({});
        }}
      />
      <InfoPopup
        open={deleteId ? true : false}
        handleClose={() => setDeleteId("")}
        onClickBtn1={() => setDeleteId("")}
        onClickBtn2={handleDelete}
        loading2={isDeleteUserSucceeded}
        btn1={"cancel"}
        btn1Variant={"outline"}
        btn2={"Delete"}
        title={"Debate base"}
        description={`Are you sure you want to \n remove this user?`}
      />
    </div>
  );
}

export default RoleManagementTableFactored;
